/* ============================================================
   CHEMARRY CONNECT
   La Casa de Chemarry
   JavaScript principal
   ============================================================ */

(() => {
  'use strict';

  const SELECTORS = {
    section: '.chemarry-connect',
    revealButton: '[data-cc-reveal-benefit]',
    benefitIntro: '[data-cc-benefit-intro]',
    benefitContent: '[data-cc-benefit-content]',
    copyButton: '[data-cc-copy-code]',
    discountCode: '[data-cc-discount-code]'
  };


  /* ============================================================
     COPIAR TEXTO AL PORTAPAPELES
     ============================================================ */

  async function copyToClipboard(text) {
    if (!text) return false;

    try {
      if (
        navigator.clipboard &&
        typeof navigator.clipboard.writeText === 'function' &&
        window.isSecureContext
      ) {
        await navigator.clipboard.writeText(text);
        return true;
      }
    } catch (error) {
      console.warn(
        '[Chemarry Connect] Clipboard API no disponible.',
        error
      );
    }

    try {
      const textarea = document.createElement('textarea');

      textarea.value = text;

      textarea.setAttribute('readonly', '');

      textarea.style.position = 'fixed';
      textarea.style.top = '-9999px';
      textarea.style.left = '-9999px';
      textarea.style.opacity = '0';

      document.body.appendChild(textarea);

      textarea.focus();
      textarea.select();

      const copied = document.execCommand('copy');

      document.body.removeChild(textarea);

      return copied;
    } catch (error) {
      console.warn(
        '[Chemarry Connect] No fue posible copiar el código.',
        error
      );

      return false;
    }
  }


  /* ============================================================
     REVELAR BENEFICIO
     ============================================================ */

  function revealBenefit(section) {
    if (!section) return;

    const revealButton = section.querySelector(
      SELECTORS.revealButton
    );

    const benefitIntro = section.querySelector(
      SELECTORS.benefitIntro
    );

    const benefitContent = section.querySelector(
      SELECTORS.benefitContent
    );

    if (!benefitContent) return;

    if (benefitIntro) {
      benefitIntro.hidden = true;
    }

    benefitContent.hidden = false;

    requestAnimationFrame(() => {
      benefitContent.classList.add('is-visible');
    });

    if (revealButton) {
      revealButton.setAttribute(
        'aria-expanded',
        'true'
      );
    }

    section.dataset.benefitRevealed = 'true';

    try {
      sessionStorage.setItem(
        `chemarry-connect-benefit-${section.dataset.sectionId}`,
        'revealed'
      );
    } catch (error) {
      /* sessionStorage puede estar bloqueado.
         La landing seguirá funcionando normalmente. */
    }
  }


  /* ============================================================
     RESTAURAR BENEFICIO REVELADO
     ============================================================ */

  function restoreBenefitState(section) {
    if (!section) return;

    try {
      const state = sessionStorage.getItem(
        `chemarry-connect-benefit-${section.dataset.sectionId}`
      );

      if (state === 'revealed') {
        revealBenefit(section);
      }
    } catch (error) {
      /* No hacemos nada.
         No es necesario para el funcionamiento principal. */
    }
  }


  /* ============================================================
     BOTÓN COPIAR
     ============================================================ */

  async function handleCopy(section, button) {
    if (!section || !button) return;

    const codeElement = section.querySelector(
      SELECTORS.discountCode
    );

    const code =
      codeElement?.textContent?.trim() ||
      section.dataset.discountCode?.trim();

    if (!code) return;

    const copied = await copyToClipboard(code);

    if (!copied) return;

    if (!button.dataset.originalLabel) {
      button.dataset.originalLabel =
        button.textContent.trim();
    }

    button.textContent = '¡Copiado!';

    button.classList.add('is-copied');

    button.setAttribute(
      'aria-label',
      `Código ${code} copiado`
    );

    window.clearTimeout(
      Number(button.dataset.resetTimer)
    );

    const resetTimer = window.setTimeout(() => {
      button.textContent =
        button.dataset.originalLabel || 'Copiar';

      button.classList.remove('is-copied');

      button.removeAttribute('aria-label');
    }, 1800);

    button.dataset.resetTimer =
      String(resetTimer);
  }


  /* ============================================================
     EVENTOS DE UNA SECCIÓN
     ============================================================ */

  function initializeSection(section) {
    if (!section) return;

    if (section.dataset.ccInitialized === 'true') {
      return;
    }

    section.dataset.ccInitialized = 'true';

    restoreBenefitState(section);

    section.addEventListener('click', async (event) => {

      const revealButton = event.target.closest(
        SELECTORS.revealButton
      );

      if (revealButton) {
        event.preventDefault();

        revealBenefit(section);

        return;
      }


      const copyButton = event.target.closest(
        SELECTORS.copyButton
      );

      if (copyButton) {
        event.preventDefault();

        await handleCopy(
          section,
          copyButton
        );

        return;
      }

    });
  }


  /* ============================================================
     INICIALIZAR TODAS LAS SECCIONES
     ============================================================ */

  function initializeAll() {
    document
      .querySelectorAll(SELECTORS.section)
      .forEach((section) => {
        initializeSection(section);
      });
  }


  /* ============================================================
     CARGA NORMAL
     ============================================================ */

  if (document.readyState === 'loading') {

    document.addEventListener(
      'DOMContentLoaded',
      initializeAll,
      { once: true }
    );

  } else {

    initializeAll();

  }


  /* ============================================================
     SHOPIFY THEME EDITOR
     Permite que siga funcionando al editar la sección
     sin tener que recargar toda la página.
     ============================================================ */

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      const section =
        event.target.matches?.(SELECTORS.section)
          ? event.target
          : event.target.querySelector?.(
              SELECTORS.section
            );

      if (section) {
        initializeSection(section);
      }

    }
  );


  /* ============================================================
     SHOPIFY BLOCK SELECT
     Si seleccionamos un bloque en el editor,
     lo llevamos suavemente a pantalla.
     ============================================================ */

  document.addEventListener(
    'shopify:block:select',
    (event) => {

      const block = event.target;

      if (!block) return;

      window.requestAnimationFrame(() => {

        block.scrollIntoView({
          behavior: 'smooth',
          block: 'center'
        });

      });

    }
  );

})();